import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Loader2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { GoogleSignInButton, AuthDivider } from "@/components/GoogleSignInButton";

const AuthShell = ({ eyebrow, title, subtitle, children }) => (
  <div className="min-h-screen bg-[#020617] flex flex-col">
    <div
      className="pointer-events-none fixed inset-0"
      style={{
        background:
          "radial-gradient(45rem 30rem at 80% 0%, rgba(212,175,55,0.12), transparent 60%), radial-gradient(40rem 28rem at 0% 100%, rgba(56,89,163,0.16), transparent 65%)",
      }}
    />
    <div className="relative px-6 py-6">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-slate-500 hover:text-[#D4AF37] text-xs uppercase tracking-widest transition-colors"
        data-testid="auth-back-home"
      >
        <ArrowLeft size={14} /> Bad Boss Clinic
      </Link>
    </div>
    <div className="relative flex-1 flex items-center justify-center px-6 pb-16">
      <div className="w-full max-w-md" style={{ animation: "fadeInUp 0.5s ease-out both" }}>
        <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-4">{eyebrow}</p>
        <h1 className="font-serif text-3xl md:text-4xl text-white mb-3">{title}</h1>
        <p className="text-slate-400 text-sm mb-9 leading-relaxed">{subtitle}</p>
        {children}
        <p className="text-slate-600 text-xs mt-10" data-testid="auth-entity-line">
          Bad Boss Clinic is a service of Apex Advisory.
        </p>
      </div>
    </div>
  </div>
);

const field =
  "w-full bg-[#0B1220] border border-slate-800 px-4 py-3 text-white text-sm placeholder:text-slate-600 focus:border-[#D4AF37] focus:outline-none transition-colors";
const label = "block text-xs uppercase tracking-widest text-slate-500 mb-2";
const primaryBtn =
  "w-full bg-[#D4AF37] text-slate-950 py-3.5 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#B5952F] disabled:opacity-60 transition-colors flex items-center justify-center gap-2";

export const PortalLogin = () => {
  const { login, user, checking } = usePortalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const checkoutPlan = params.get("checkout");
  const postAuthPath = checkoutPlan ? `/portal/pricing?checkout=${checkoutPlan}` : (location.state?.from || "/portal");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!checking && user) navigate(postAuthPath, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checking, user]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(postAuthPath, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Client Portal"
      title="Welcome back"
      subtitle="Sign in to continue your assessments, incident log and vulnerability report."
    >
      <GoogleSignInButton returnPath="/portal" testid="login-google-btn" />
      <AuthDivider />
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div>
          <label className={label} htmlFor="login-email">Email</label>
          <input
            id="login-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={field}
            placeholder="you@company.com"
            data-testid="login-email-input"
          />
        </div>
        <div>
          <label className={label} htmlFor="login-password">Password</label>
          <div className="relative">
            <input
              id="login-password"
              type={show ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={field}
              placeholder="••••••••"
              data-testid="login-password-input"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              data-testid="login-toggle-password"
            >
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        {error && (
          <p className="text-red-400 text-sm border border-red-500/30 bg-red-500/10 px-4 py-3" data-testid="login-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={loading} className={primaryBtn} data-testid="login-submit-btn">
          {loading && <Loader2 className="animate-spin" size={14} />}
          {loading ? "Signing in" : "Sign in"}
        </button>
      </form>
      <p className="mt-5 text-center">
        <Link to="/portal/forgot-password" className="text-slate-500 hover:text-[#D4AF37] text-xs uppercase tracking-widest transition-colors" data-testid="login-forgot-link">
          Forgot your password?
        </Link>
      </p>
      <p className="mt-7 text-center text-sm text-slate-500">
        No account yet?{" "}
        <Link to={checkoutPlan ? `/portal/register?checkout=${checkoutPlan}` : "/portal/register"} state={{ from: location.state?.from }} className="text-[#D4AF37] hover:underline" data-testid="login-to-register">
          Create one
        </Link>
      </p>
    </AuthShell>
  );
};

export const PortalRegister = () => {
  const { register, user, checking } = usePortalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const referralCode = params.get("ref") || "";
  const checkoutPlan = params.get("checkout");
  const postAuthPath = checkoutPlan
    ? `/portal/pricing?checkout=${checkoutPlan}`
    : (location.state?.from || "/portal/onboarding");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!checking && user) {
      navigate(checkoutPlan ? `/portal/pricing?checkout=${checkoutPlan}` : (location.state?.from || "/portal"), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checking, user]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(form.name, form.email, form.password, referralCode);
      navigate(postAuthPath, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Client Portal"
      title="Create your account"
      subtitle={checkoutPlan
        ? "Create your account to start Premium — you'll go straight to secure checkout next."
        : "Two validated assessments, a private incident log and an AI advisor trained on the clinic's research. Free to start."}
    >
      {referralCode && (
        <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/[0.06] px-4 py-3 mb-6" data-testid="register-referral-note">
          <p className="text-slate-300 text-sm">
            You were invited — get <span className="text-[#D4AF37]">50% off your first month</span> of Premium
            (or 10% off your first year). Your colleague earns a free month when you upgrade or book your first session.
          </p>
        </div>
      )}
      <GoogleSignInButton returnPath="/portal" label="Sign up with Google" testid="register-google-btn" />
      <AuthDivider text="or sign up with email" />
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <div>
          <label className={label} htmlFor="register-name">Full name</label>
          <input id="register-name" required value={form.name} onChange={set("name")} className={field} placeholder="Alex Moreau" data-testid="register-name-input" />
        </div>
        <div>
          <label className={label} htmlFor="register-email">Email</label>
          <input id="register-email" type="email" required value={form.email} onChange={set("email")} className={field} placeholder="you@company.com" data-testid="register-email-input" />
        </div>
        <div>
          <label className={label} htmlFor="register-password">Password</label>
          <div className="relative">
            <input
              id="register-password"
              type={show ? "text" : "password"}
              required
              value={form.password}
              onChange={set("password")}
              className={field}
              placeholder="At least 8 characters"
              data-testid="register-password-input"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              data-testid="register-toggle-password"
            >
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        {error && (
          <p className="text-red-400 text-sm border border-red-500/30 bg-red-500/10 px-4 py-3" data-testid="register-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={loading} className={primaryBtn} data-testid="register-submit-btn">
          {loading && <Loader2 className="animate-spin" size={14} />}
          {loading ? "Creating account" : "Create account"}
        </button>
      </form>
      <p className="mt-7 text-center text-sm text-slate-500">
        Already registered?{" "}
        <Link to={checkoutPlan ? `/portal/login?checkout=${checkoutPlan}` : "/portal/login"} state={{ from: location.state?.from }} className="text-[#D4AF37] hover:underline" data-testid="register-to-login">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
};
